import React, { useState } from 'react';
import { FaEdit, FaTrash, FaPlus, FaStar, FaRegStar } from 'react-icons/fa';
import { FaChartGantt } from 'react-icons/fa6';
import { Link } from 'react-router-dom';
import { Card, CardHeader, CardContent } from '../ui/card';
import AddTaskModal from '../tasks/AddTaskModal';
import EditProjectModal from './EditProjectModal';
import DeleteProjectModal from './DeleteProjectModal';
import { useToast } from '../../contexts/ToastContext';
import { supabase } from '../../supabaseClient';

function ProjectCard({ 
  project, 
  viewMode = 'card', // 'card' or 'list'
  onProjectUpdated, 
  onProjectDeleted, 
  onToggleFavorite 
}) {
  const [showAddTaskModal, setShowAddTaskModal] = useState(false);
  const [showEditProjectModal, setShowEditProjectModal] = useState(false);
  const [showDeleteProjectModal, setShowDeleteProjectModal] = useState(false);
  const addToast = useToast();

  const handleAddTask = async (task) => {
    const { data, error } = await supabase
      .from('tasks')
      .insert([{ ...task, project_id: project.id }])
      .single();

    if (error) {
      console.error('Error adding task:', error);
      addToast('Error adding task: ' + error.message, 'danger');
    } else {
      addToast('Task added successfully', 'success');
      onProjectUpdated({ ...project, tasks: [...project.tasks, data] });
    }
  };

  const handleEditProject = async (updatedProject) => {
    const { data, error } = await supabase
      .from('projects')
      .update(updatedProject)
      .eq('id', project.id)
      .single();

    if (error) {
      console.error('Error updating project:', error);
      addToast('Error updating project: ' + error.message, 'danger');
    } else {
      addToast('Project updated successfully', 'success');
      onProjectUpdated(data);
    }
  };

  const handleDeleteProject = async () => {
    const { error } = await supabase
      .from('projects')
      .delete()
      .eq('id', project.id);

    if (error) {
      console.error('Error deleting project:', error);
      addToast('Error deleting project: ' + error.message, 'error');
    } else {
      addToast('Project deleted successfully', 'success');
      onProjectDeleted(project.id);
    }
  };

  const handleToggleFavorite = () => {
    onToggleFavorite(project.id, project.is_favorite);
  };

  const cardClasses = viewMode === 'list' 
    ? 'mb-2'
    : 'mb-3';

  const cardStyle = viewMode === 'list' 
    ? { minHeight: '80px' }
    : { minHeight: '200px' };

  return (
    <>
      <Card className={`project-card ${cardClasses} hover:shadow-lg transition-shadow`} style={cardStyle}>
        <CardHeader className={viewMode === 'list' ? 'py-2' : ''}>
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <FaChartGantt className="mr-2 text-blue-600 dark:text-blue-400" />
              <Link 
                to={`/project/${project.id}`} 
                className="text-gray-900 dark:text-gray-100 hover:text-blue-600 dark:hover:text-blue-400 no-underline font-medium"
              >
                {project.name}
              </Link>
            </div>
            <div className="flex items-center gap-2">
              <Link 
                to={`/project/${project.id}`}
                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 no-underline"
              >
                {project.tasks.length} tasks
              </Link>
              <button 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleToggleFavorite();
                }}
                className="p-2 text-gray-500 hover:text-yellow-500 hover:bg-yellow-50 dark:text-gray-400 dark:hover:text-yellow-400 dark:hover:bg-yellow-900/20 transition-colors rounded"
                title={project.is_favorite ? "Remove from favorites" : "Add to favorites"}
              >
                {project.is_favorite ? <FaStar className="text-yellow-500" /> : <FaRegStar />}
              </button>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className={viewMode === 'list' ? 'py-2' : ''}>
          {viewMode === 'card' && project.description && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              {project.description}
            </p>
          )}
          
          <div className={`flex justify-between items-center ${viewMode === 'list' ? 'mt-1' : ''}`}>
            <div className="flex items-center gap-2">
              <Link 
                to={`/project-group/${project.project_groups.id}`}
                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300 no-underline"
              >
                {project.project_groups.name}
              </Link>
              {project.tags && project.tags.split(',').map((tag, index) => (
                <span 
                  key={index}
                  className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                >
                  {tag.trim()}
                </span>
              ))}
            </div>
            <div className="flex items-center gap-1">
              <button 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowAddTaskModal(true);
                }}
                className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 dark:text-gray-400 dark:hover:text-green-400 dark:hover:bg-green-900/20 transition-colors rounded"
                title="Add Task"
              >
                <FaPlus />
              </button>
              <button 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowEditProjectModal(true);
                }}
                className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:text-gray-400 dark:hover:text-blue-400 dark:hover:bg-blue-900/20 transition-colors rounded"
                title="Edit Project"
              >
                <FaEdit />
              </button>
              <button 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowDeleteProjectModal(true);
                }}
                className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:text-gray-400 dark:hover:text-red-400 dark:hover:bg-red-900/20 transition-colors rounded"
                title="Delete Project"
              >
                <FaTrash />
              </button>
            </div>
          </div>
        </CardContent>
      </Card>

      {showAddTaskModal && (
        <AddTaskModal
          show={showAddTaskModal}
          handleClose={() => setShowAddTaskModal(false)}
          projectId={project.id}
          onTaskAdded={handleAddTask}
        />
      )}
      {showEditProjectModal && (
        <EditProjectModal
          show={showEditProjectModal}
          handleClose={() => setShowEditProjectModal(false)}
          project={project}
          onProjectUpdated={handleEditProject}
        />
      )}
      {showDeleteProjectModal && (
        <DeleteProjectModal
          show={showDeleteProjectModal}
          handleClose={() => setShowDeleteProjectModal(false)}
          project={project}
          onProjectDeleted={handleDeleteProject}
        />
      )}
    </>
  );
}

export default ProjectCard;